import { Method } from "axios";
import api from "@/shared/api/api";
import { AppGranularity, AppPlatform, AppAnalyticsSummary, ChartResponse, GeographyResponse, ProductsResponse, UsageSummary, AppProduct } from "../types";
import { createAppFiguresClient } from "./appFiguresClient";

export interface BaseParams {
  start: string;
  end: string;
  platform?: AppPlatform;
  granularity?: AppGranularity;
}

type ActionBuilder<P, R> = (params: P) => () => Promise<R>;

/**
 * CONFIGURAÇÃO DE FONTE DE DADOS
 * 
 * Atualmente consumindo DIRETO do AppFigures.
 * 
 * Para trocar para o backend:
 * 1. Mude USE_BACKEND para true
 * 2. Configure VITE_API_BASE_URL no .env para apontar para seu backend
 * 3. Garanta que seu backend retorne as mesmas interfaces de dados
 */
const USE_BACKEND = false; // Trocar para true quando o backend estiver pronto

export default class AppAnalyticsService {
  [key: string]: { key: string; actionBuilder: (params: any) => () => Promise<any> } | unknown;
  static Instance = new AppAnalyticsService();
  private client = createAppFiguresClient();

  private getUsageNetworks(platform?: AppPlatform | string): string[] {
    if (platform === "ios") {
      return ["apple:analytics"];
    }

    if (platform === "android") {
      return ["google_play_console"];
    }

    return ["apple:analytics", "google_play_console"];
  }

  private getUsageProductsFilter(platform?: AppPlatform | string): string | undefined {
    if (!platform || platform === "all" || platform === "ios" || platform === "android") {
      return undefined;
    }

    return platform;
  }

  private async fetchUsageData(params: BaseParams, groupBy: "network" | "network,date") {
    const networks = this.getUsageNetworks(params.platform);
    const productsFilter = this.getUsageProductsFilter(params.platform);

    const baseRequest = {
      start_date: params.start,
      end_date: params.end,
      group_by: groupBy,
      ...(productsFilter ? { products: productsFilter } : {}),
    };

    const responses = await Promise.all(
      networks.map((network) =>
        this.client.getUsage({
          ...baseRequest,
          networks: network,
        })
      )
    );

    const combined: Record<string, unknown> = {};

    responses.forEach((response) => {
      if (response && typeof response === "object") {
        Object.entries(response as Record<string, unknown>).forEach(([networkKey, data]) => {
          combined[networkKey] = data;
        });
      }
    });

    return combined;
  }

  private async callApi<R>(endpoint: string, params: Record<string, unknown>): Promise<R> {
    const response = await api<R>({ url: endpoint, method: "GET" as Method, params });
    return response;
  }

  summary: { key: string; actionBuilder: ActionBuilder<BaseParams, AppAnalyticsSummary> } = {
    key: "app-analytics-summary",
    actionBuilder: (params) => async () => {
      if (USE_BACKEND) {
        return this.callApi<AppAnalyticsSummary>(`/v1/app-analytics/summary`, params as unknown as Record<string, unknown>);
      }

      // Chama AppFigures direto
      const { start, end, platform } = params;
      const [revenue, subscriptions, sales, usage] = await Promise.all([
        this.client.getRevenue({ start_date: start, end_date: end, ...(platform !== "all" && { products: platform }) }),
        this.client.getSubscriptions({ start_date: start, end_date: end, ...(platform !== "all" && { products: platform }) }),
        this.client.getSales({ start_date: start, end_date: end, ...(platform !== "all" && { products: platform }) }),
        this.client.getUsage({ start_date: start, end_date: end, group_by: "network", ...(platform !== "all" && { products: platform }) }),
      ]);

      return {
        period: { start, end },
        revenue: { total: revenue?.total ?? 0 },
        subscriptions: { total: subscriptions?.total ?? 0 },
        churnRate: { total: 0 },
        trials: { total: 0 },
        trialConversions: { total: 0 },
        downloads: { total: sales?.downloads ?? 0 },
        freeUsers: { total: usage?.active_users ?? 0 },
      } as AppAnalyticsSummary;
    },
  };

  subscriptionsSummary: {
    key: string; actionBuilder: ActionBuilder<BaseParams, {
      active_subscriptions: number;
      active_free_trials: number;
      new_subscriptions: number;
      cancelled_subscriptions: number;
      trial_conversions: number;
      churn: number | string;
      mrr: number | string;
      actual_revenue: number | string;
      gross_revenue: number | string;
    }>
  } = {
      key: "app-analytics-subscriptions-summary",
      actionBuilder: (params) => async () => {
        if (USE_BACKEND) {
          return this.callApi(`/v1/app-analytics/subscriptions/summary`, params as unknown as Record<string, unknown>);
        }

        // Chama AppFigures direto
        const { start, end, platform } = params;
        const subscriptionParams: any = {
          start_date: start,
          end_date: end,
        };

        if (platform !== "all") {
          subscriptionParams.products = platform;
          subscriptionParams.include_inapps = true;
        }

        const data = await this.client.getSubscriptions(subscriptionParams);
        const num = (v: unknown) => Number.isFinite(Number(v)) ? Number(v) : 0;
        const pick = (k: string) => (data && (data as Record<string, unknown>)[k] !== undefined ? (data as Record<string, unknown>)[k] : 0);

        return {
          active_subscriptions: num(pick("active_subscriptions")),
          active_free_trials: num(pick("active_free_trials")),
          new_subscriptions: num(pick("new_subscriptions")),
          cancelled_subscriptions: num(pick("cancelled_subscriptions")),
          trial_conversions: num(pick("trial_conversions")),
          churn: pick("churn") as number | string,
          mrr: pick("mrr") as number | string,
          actual_revenue: pick("actual_revenue") as number | string,
          gross_revenue: pick("gross_revenue") as number | string,
        };
      },
    };

  revenueSummary: {
    key: string; actionBuilder: ActionBuilder<BaseParams, {
      total: number;
      subscriptions: number;
      iap: number;
      sales: number;
      returns: number;
      gross_total: number;
    }>
  } = {
      key: "app-analytics-revenue-summary",
      actionBuilder: (params) => async () => {
        if (USE_BACKEND) {
          return this.callApi(`/v1/app-analytics/revenue/summary`, params as unknown as Record<string, unknown>);
        }

        // Chama AppFigures direto
        const { start, end, platform } = params;
        const revenue = await this.client.getRevenue({
          start_date: start,
          end_date: end,
          ...(platform !== "all" && { products: platform })
        });

        const toNum = (v: unknown) => Number.isFinite(Number(v)) ? Number(v) : 0;

        return {
          total: toNum(revenue?.total),
          subscriptions: toNum(revenue?.subscriptions),
          iap: toNum(revenue?.iap),
          sales: toNum(revenue?.sales),
          returns: toNum(revenue?.returns) * -1,
          gross_total: toNum(revenue?.gross_total),
        };
      },
    };

  revenueTimeseries: { key: string; actionBuilder: ActionBuilder<BaseParams, ChartResponse> } = {
    key: "app-analytics-revenue-timeseries",
    actionBuilder: (params) => async () => {
      if (USE_BACKEND) {
        return this.callApi<ChartResponse>(`/v1/app-analytics/revenue/timeseries`, params as unknown as Record<string, unknown>);
      }

      const { start, end, platform } = params;
      const revenueByDate = await this.client.getRevenue({
        start_date: start,
        end_date: end,
        group_by: "date",
        ...(platform !== "all" && { products: platform })
      });

      const labels: string[] = [];
      const netValues: number[] = [];
      const grossValues: number[] = [];
      const returnsValues: number[] = [];

      if (revenueByDate && typeof revenueByDate === "object" && !Array.isArray(revenueByDate)) {
        const toNum = (value: unknown) => {
          if (value === null || value === undefined) return 0;
          const parsed = typeof value === "string" ? parseFloat(value) : Number(value);
          return Number.isFinite(parsed) ? parsed : 0;
        };

        Object.entries(revenueByDate)
          .filter(([date]) => /^\d{4}-\d{2}-\d{2}$/.test(date))
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([date, values]) => {
            if (values && typeof values === "object") {
              const entry = values as Record<string, unknown>;
              const gross = toNum(entry.gross_total ?? entry.gross_revenue ?? entry.gross);
              const net = toNum(entry.total ?? entry.net);
              const returnsRaw = toNum(entry.returns);

              labels.push(date);
              grossValues.push(gross);
              netValues.push(net);
              returnsValues.push(Math.abs(returnsRaw));
            }
          });
      }

      return {
        labels,
        series: [
          { name: "Net Revenue", values: netValues },
          { name: "Gross Revenue", values: grossValues },
          { name: "Returns", values: returnsValues }
        ],
      };
    },
  };

  timeseries: { key: string; actionBuilder: ActionBuilder<BaseParams & { metric: string }, ChartResponse> } = {
    key: "app-analytics-timeseries",
    actionBuilder: (params) => async () => {
      if (USE_BACKEND) {
        return this.callApi<ChartResponse>(`/v1/app-analytics/timeseries`, params as unknown as Record<string, unknown>);
      }

      // AppFigures direto - não implementado (não usado no frontend atualmente)
      throw new Error("timeseries direto não implementado - use endpoints específicos");
    },
  };

  geography: { key: string; actionBuilder: ActionBuilder<BaseParams, GeographyResponse> } = {
    key: "app-analytics-geography",
    actionBuilder: (params) => async () => {
      if (USE_BACKEND) {
        return this.callApi<GeographyResponse>(`/v1/app-analytics/geography`, params as unknown as Record<string, unknown>);
      }

      // Chama AppFigures direto
      const { start, end } = params;
      const sales = await this.client.getSales({
        start_date: start,
        end_date: end,
        countries: "all"
      });

      const items = (sales?.countries ?? []).map((country: any) => ({
        country: country.code,
        value: Number(country.downloads) || 0
      }));
      return { items };
    },
  };

  usageSummary: { key: string; actionBuilder: ActionBuilder<BaseParams, UsageSummary> } = {
    key: "app-analytics-usage-summary",
    actionBuilder: (params) => async () => {
      if (USE_BACKEND) {
        return this.callApi<UsageSummary>(`/v1/app-analytics/usage/summary`, params as unknown as Record<string, unknown>);
      }

      // Chama AppFigures direto
      const { start, end, platform } = params;
      const usage = await this.fetchUsageData({ start, end, platform }, "network");

      const toNum = (v: unknown) => Number.isFinite(Number(v)) ? Number(v) : 0;

      // Agregar dados de todos os networks
      let totalSessions = 0;
      let totalCrashes = 0;
      let totalDailyActiveDevices = 0;
      let totalMonthlyActiveDevices = 0;
      let totalSessionsPerDevice = 0;
      let totalAvgOptinRate = 0;
      let totalPayingUsers = 0;
      let totalAppStoreViews = 0;
      let totalUniqueAppStoreViews = 0;
      let totalImpressions = 0;
      let totalUniqueImpressions = 0;
      let networkCount = 0;

      if (usage && typeof usage === 'object') {
        Object.values(usage).forEach((networkData: any) => {
          if (networkData && typeof networkData === 'object') {
            networkCount++;
            totalSessions += toNum(networkData.sessions);
            totalCrashes += toNum(networkData.crashes);
            totalDailyActiveDevices += toNum(networkData.daily_active_devices);
            totalMonthlyActiveDevices += toNum(networkData.monthly_active_devices);
            totalSessionsPerDevice += toNum(networkData.sessions_per_device);
            totalAvgOptinRate += toNum(networkData.avg_optin_rate);
            totalPayingUsers += toNum(networkData.paying_users);
            totalAppStoreViews += toNum(networkData.app_store_views);
            totalUniqueAppStoreViews += toNum(networkData.unique_app_store_views);
            totalImpressions += toNum(networkData.impressions);
            totalUniqueImpressions += toNum(networkData.unique_impressions);
          }
        });
      }

      const avgSessionsPerDevice = networkCount > 0 ? totalSessionsPerDevice / networkCount : 0;
      const avgOptinRate = networkCount > 0 ? totalAvgOptinRate / networkCount : 0;

      return {
        sessions: totalSessions,
        crashes: totalCrashes,
        daily_active_devices: totalDailyActiveDevices,
        monthly_active_devices: totalMonthlyActiveDevices,
        sessions_per_device: Math.round(avgSessionsPerDevice * 100) / 100,
        avg_optin_rate: Math.round(avgOptinRate * 10000) / 100,
        paying_users: totalPayingUsers,
        app_store_views: totalAppStoreViews,
        unique_app_store_views: totalUniqueAppStoreViews,
        impressions: totalImpressions,
        unique_impressions: totalUniqueImpressions,
      };
    },
  };

  usageTimeseriesBundle: {
    key: string; actionBuilder: ActionBuilder<BaseParams, {
      labels: string[];
      series: {
        sessions: number[];
        dailyActiveDevices: number[];
        crashes: number[];
        payingUsers: number[];
        appStoreViews: number[];
      };
    }>
  } = {
      key: "app-analytics-usage-timeseries-bundle",
      actionBuilder: (params) => async () => {
        if (USE_BACKEND) {
          return this.callApi(`/v1/app-analytics/usage/timeseries/bundle`, params as unknown as Record<string, unknown>);
        }

        // Chama AppFigures direto
        const { start, end, platform } = params;
        const usage = await this.fetchUsageData({ start, end, platform }, "network,date");

        const dateMap = new Map<string, {
          sessions: number;
          dailyActiveDevices: number;
          crashes: number;
          payingUsers: number;
          appStoreViews: number;
        }>();

        if (usage && typeof usage === 'object' && !Array.isArray(usage)) {
          Object.values(usage).forEach((networkData: any) => {
            if (networkData && typeof networkData === 'object') {
              Object.entries(networkData).forEach(([date, data]: [string, unknown]) => {
                if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  const existing = dateMap.get(date) || {
                    sessions: 0,
                    dailyActiveDevices: 0,
                    crashes: 0,
                    payingUsers: 0,
                    appStoreViews: 0,
                  };

                  if (data && typeof data === 'object') {
                    const dataObj = data as Record<string, unknown>;
                    existing.sessions += Number(dataObj.sessions) || 0;
                    existing.dailyActiveDevices += Number(dataObj.daily_active_devices) || 0;
                    existing.crashes += Number(dataObj.crashes) || 0;
                    existing.payingUsers += Number(dataObj.paying_users) || 0;
                    existing.appStoreViews += Number(dataObj.app_store_views) || 0;
                  }

                  dateMap.set(date, existing);
                }
              });
            }
          });
        }

        const labels: string[] = [];
        const sessionsValues: number[] = [];
        const devicesValues: number[] = [];
        const crashesValues: number[] = [];
        const payingUsersValues: number[] = [];
        const appStoreViewsValues: number[] = [];

        Array.from(dateMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([date, values]) => {
            labels.push(date);
            sessionsValues.push(values.sessions);
            devicesValues.push(values.dailyActiveDevices);
            crashesValues.push(values.crashes);
            payingUsersValues.push(values.payingUsers);
            appStoreViewsValues.push(values.appStoreViews);
          });

        return {
          labels,
          series: {
            sessions: sessionsValues,
            dailyActiveDevices: devicesValues,
            crashes: crashesValues,
            payingUsers: payingUsersValues,
            appStoreViews: appStoreViewsValues,
          },
        };
      },
    };

  subscriptionsTimeseries: { key: string; actionBuilder: ActionBuilder<BaseParams, ChartResponse> } = {
    key: "app-analytics-subscriptions-timeseries",
    actionBuilder: (params) => async () => {
      if (USE_BACKEND) {
        return this.callApi<ChartResponse>(`/v1/app-analytics/subscriptions/timeseries`, params as unknown as Record<string, unknown>);
      }

      // Chama AppFigures direto com group_by=date
      const { start, end, platform } = params;
      const subscriptionParams: any = {
        start_date: start,
        end_date: end,
        group_by: "date",
      };

      if (platform !== "all") {
        subscriptionParams.products = platform;
        subscriptionParams.include_inapps = true;
      }

      const data = await this.client.getSubscriptions(subscriptionParams);

      const labels: string[] = [];
      const activeValues: number[] = [];
      const newValues: number[] = [];
      const cancelledValues: number[] = [];

      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // A API retorna um objeto com datas como chaves
        Object.entries(data)
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([date, dateData]: [string, unknown]) => {
            if (date.match(/^\d{4}-\d{2}-\d{2}$/) && dateData && typeof dateData === 'object') {
              const obj = dateData as Record<string, unknown>;
              labels.push(date);
              activeValues.push(Number(obj.active_subscriptions) || 0);
              newValues.push(Number(obj.new_subscriptions) || 0);
              cancelledValues.push(Number(obj.cancelled_subscriptions) || 0);
            }
          });
      }

      return {
        labels,
        series: [
          { name: "Active Subscriptions", values: activeValues },
          { name: "New Subscriptions", values: newValues },
          { name: "Cancelled Subscriptions", values: cancelledValues }
        ]
      };
    },
  };

  products: { key: string; actionBuilder: ActionBuilder<Record<string, never>, ProductsResponse> } = {
    key: "app-analytics-products",
    actionBuilder: () => async () => {
      if (USE_BACKEND) {
        return this.callApi<ProductsResponse>(`/v1/app-analytics/products`, {});
      }

      // Chama AppFigures direto
      const products = await this.client.getProducts({ include_inactive: false });

      // Filtrar apenas produtos principais (sem parent_id)
      const mainProducts: ProductsResponse = {};
      Object.entries(products).forEach(([key, product]: [string, unknown]) => {
        if (!(product as AppProduct).parent_id) {
          mainProducts[key] = product as AppProduct;
        }
      });

      return mainProducts;
    },
  };
}
